import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Luggage, User, Hotel, BedDouble, AlertCircle, Phone } from 'lucide-react';
import useFetch from '../../hooks/useFetch';
import Logo from '../../components/Logo';
import { Badge, Loader } from '../../components/UI';

/**
 * What a printed bag tag's QR opens. No login: whoever is holding the bag needs
 * to know whose it is and where it goes. Deliberately shows nothing more.
 */
export default function LuggageTag() {
  const { tag } = useParams();
  const { data, loading, error } = useFetch(`/luggage/public/${tag}`);

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', padding: '32px 20px',
    }}>
      <Logo size={40} showText />

      {loading ? <Loader full label="Looking up this tag" /> : error || !data ? (
        <div style={{ textAlign: 'center', maxWidth: 380, marginTop: 30 }}>
          <AlertCircle size={30} color="var(--warn)" />
          <h1 style={{ fontSize: 19, fontWeight: 600, marginTop: 14 }}>Tag not recognised</h1>
          <p style={{ fontSize: 13.5, color: 'var(--text-mid)', marginTop: 8, lineHeight: 1.7 }}>
            {error || 'We could not find a bag with this tag. Please check the code and try again.'}
          </p>
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          style={{
            width: '100%', maxWidth: 400, marginTop: 26, overflow: 'hidden',
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 'var(--r-xl)',
          }}
        >
          <div style={{
            padding: '20px 22px', textAlign: 'center',
            background: 'linear-gradient(135deg, #1A1A22, #121218)',
            borderBottom: '1px solid var(--border)',
          }}>
            <Luggage size={24} color="var(--gold-400)" />
            <p style={{
              fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700,
              color: 'var(--gold-300)', letterSpacing: 1, marginTop: 9,
            }}>
              {data.tagNumber}
            </p>
            <Badge tone="neutral" style={{ marginTop: 9 }}>{data.status?.replace('_', ' ')}</Badge>
          </div>

          <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 18 }}>
            <Line icon={User} label="Belongs to" value={data.guest.name}
              sub={[data.guest.employeeCode, data.guest.designation].filter(Boolean).join(' · ')} />

            <Line icon={Hotel} label="Hotel"
              value={data.hotel?.name || 'Not assigned yet'}
              muted={!data.hotel}
              sub={data.hotel?.contactMobile} />

            <Line icon={BedDouble} label="Room"
              value={data.roomNumber || 'Not allocated yet'}
              muted={!data.roomNumber}
              sub={data.roomNumber ? null : 'This page updates once the hotel allocates a room.'} />

            {data.hotel?.contactMobile && (
              <a href={`tel:${data.hotel.contactMobile}`} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9,
                padding: '12px 16px', borderRadius: 'var(--r)', fontSize: 13.5, fontWeight: 600,
                background: 'var(--gold-400)', color: 'var(--ink-900)',
              }}>
                <Phone size={15} /> Call the front desk
              </a>
            )}
          </div>
        </motion.div>
      )}

      <p style={{ fontSize: 11.5, color: 'var(--text-lo)', marginTop: 22, textAlign: 'center' }}>
        Found this bag? Please hand it to the nearest Golden Lotus desk.
      </p>
    </div>
  );
}

function Line({ icon: Icon, label, value, sub, muted }) {
  return (
    <div style={{ display: 'flex', gap: 13, alignItems: 'flex-start' }}>
      <div style={{
        width: 34, height: 34, flexShrink: 0, borderRadius: 'var(--r-sm)',
        background: 'var(--ink-800)', display: 'grid', placeItems: 'center',
      }}>
        <Icon size={15} color="var(--gold-500)" />
      </div>
      <div style={{ minWidth: 0 }}>
        <p style={{
          fontSize: 10.5, color: 'var(--text-lo)', textTransform: 'uppercase',
          letterSpacing: 0.9, fontWeight: 600,
        }}>
          {label}
        </p>
        <p style={{
          fontSize: 15, fontWeight: 600, marginTop: 3,
          color: muted ? 'var(--text-lo)' : 'var(--text-hi)',
        }}>
          {value}
        </p>
        {sub && <p style={{ fontSize: 12, color: 'var(--text-lo)', marginTop: 3, lineHeight: 1.6 }}>{sub}</p>}
      </div>
    </div>
  );
}
